import { useState, useEffect } from 'react';
import { fetchWithAuth } from './authHelpers';

export const useCosmetics = () => {
    const [cosmetics, setCosmetics] = useState([]);
    const [selectedCardBackground, setSelectedCardBackground] = useState(null);
    const [selectedCardOutline, setSelectedCardOutline] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCosmetics = async () => {
            try {
                setLoading(true);
                const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/cosmetics/current`, 'GET');

                if (!response.ok) {
                    if (response.status === 404) {
                        // User has no cosmetics, set empty array
                        setCosmetics([]);
                        return;
                    }
                    throw new Error('Failed to fetch cosmetics');
                }

                const data = await response.json();
                setCosmetics(data);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        const fetchSelectedCardBackground = async () => {
            try {
                const response = await fetchWithAuth(
                    `${process.env.REACT_APP_BACKEND_URL}/cosmetics/selected-card-background`,
                    'GET'
                );

                if (!response.ok) {
                    if (response.status === 404) {
                        // User has no selected card background, set default
                        setSelectedCardBackground({
                            name: 'Default Background',
                            type: 'cardBackground'
                        });
                        return;
                    }
                    throw new Error('Failed to fetch selected card background');
                }

                const data = await response.json();
                setSelectedCardBackground(data);
            } catch (error) {
                // Set default on error
                setSelectedCardBackground({
                    name: 'Default Background',
                    type: 'cardBackground'
                });
            }
        };

        const fetchSelectedCardOutline = async () => {
            try {
                const response = await fetchWithAuth(
                    `${process.env.REACT_APP_BACKEND_URL}/cosmetics/selected-card-outline`,
                    'GET'
                );

                if (!response.ok) {
                    if (response.status === 404) {
                        // User has no selected card outline, set to null (no outline)
                        setSelectedCardOutline(null);
                        return;
                    }
                    throw new Error('Failed to fetch selected card outline');
                }

                const data = await response.json();
                setSelectedCardOutline(data);
            } catch (error) {
                // Set to null on error (no outline)
                setSelectedCardOutline(null);
            }
        };

        const initializeData = async () => {
            await Promise.all([fetchCosmetics(), fetchSelectedCardBackground(), fetchSelectedCardOutline()]);
        };

        initializeData();
    }, []);

    const refetchCosmetics = async () => {
        setError(null);
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/cosmetics/current`, 'GET');

            if (!response.ok) {
                if (response.status === 404) {
                    setCosmetics([]);
                    return;
                }
                throw new Error('Failed to fetch cosmetics');
            }

            const data = await response.json();
            setCosmetics(data);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const refetchSelectedCardBackground = async () => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_BACKEND_URL}/cosmetics/selected-card-background`,
                'GET'
            );

            if (!response.ok) {
                if (response.status === 404) {
                    setSelectedCardBackground({
                        name: 'Default Background',
                        type: 'cardBackground'
                    });
                    return;
                }
                throw new Error('Failed to fetch selected card background');
            }

            const data = await response.json();
            setSelectedCardBackground(data);
        } catch (error) {
            setSelectedCardBackground({
                name: 'Default Background',
                type: 'cardBackground'
            });
        }
    };

    const refetchSelectedCardOutline = async () => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_BACKEND_URL}/cosmetics/selected-card-outline`,
                'GET'
            );

            if (!response.ok) {
                if (response.status === 404) {
                    setSelectedCardOutline(null);
                    return;
                }
                throw new Error('Failed to fetch selected card outline');
            }

            const data = await response.json();
            setSelectedCardOutline(data);
        } catch (error) {
            setSelectedCardOutline(null);
        }
    };

    const updateSelectedCardOutline = async (cardOutlineId) => {
        try {
            setError(null);

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_BACKEND_URL}/cosmetics/selected-card-outline`,
                'PATCH',
                { cardOutlineId }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update card outline');
            }

            const data = await response.json();
            
            // Handle clearing the outline (cardOutlineId === null)
            if (cardOutlineId === null) {
                setSelectedCardOutline(null);
            } else {
                setSelectedCardOutline(data.cardOutline);
            }
            
            return data;
        } catch (error) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updateSelectedCardBackground = async (cardBackgroundId) => {
        try {
            setError(null);

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_BACKEND_URL}/cosmetics/selected-card-background`,
                'PATCH',
                { cardBackgroundId }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update card background');
            }

            const data = await response.json();
            setSelectedCardBackground(data.cardBackground);
            return data;
        } catch (error) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get cosmetics by type
    const getCosmeticsByType = (type) => {
        //Check if it's an array and filter by type
        if (!Array.isArray(cosmetics)) {
            return [];
        }
        return cosmetics?.filter(cosmetic => cosmetic.type === type);
    };

    // Helper function to check if user has a specific cosmetic
    const hasCosmetic = (cosmeticId) => {
        if (!Array.isArray(cosmetics)) {
            return false;
        }
        return cosmetics?.some(cosmetic => cosmetic._id === cosmeticId);
    };

    const getUserSelectedCardBackground = async (userNumber) => {
        try {
            // Validate userNumber before making the request
            if (!userNumber || userNumber === 'undefined' || userNumber === undefined) {
                return {
                    name: 'Default Background',
                    type: 'cardBackground'
                };
            }

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_BACKEND_URL}/cosmetics/${userNumber}/selected-card-background`,
                'GET'
            );

            if (!response.ok) {
                if (response.status === 404) {
                    return {
                        name: 'Default Background',
                        type: 'cardBackground'
                    };
                }
                throw new Error('Failed to fetch user selected card background');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            // Return default on error
            return {
                name: 'Default Background',
                type: 'cardBackground'
            };
        }
    };

    const getUserSelectedCardOutline = async (userNumber) => {
        try {
            // Validate userNumber before making the request
            if (!userNumber || userNumber === 'undefined' || userNumber === undefined) {
                return null;
            }

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_BACKEND_URL}/cosmetics/${userNumber}/selected-card-outline`,
                'GET'
            );

            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                throw new Error('Failed to fetch user selected card outline');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            // Return null on error (no outline)
            return null;
        }
    };

    return {
        cosmetics,
        selectedCardBackground,
        selectedCardOutline,
        loading,
        error,
        refetchCosmetics,
        refetchSelectedCardBackground,
        refetchSelectedCardOutline,
        updateSelectedCardBackground,
        updateSelectedCardOutline,
        getUserSelectedCardBackground,
        getUserSelectedCardOutline,
        getCosmeticsByType,
        hasCosmetic,
    };
};

export default useCosmetics;
